import { useContext } from 'react';
import { ITaskPermissions, TaskStatus } from "models/tasks";
import { TaskOptionsContext } from 'components/tasks/TaskOptionsProvider';
import { IOption, ITaskOptions, ITaskStatus } from 'models/tasks/ITaskOptions';
import { useTaskPermissions } from 'hooks/tasks';

const useTaskStatusOptions = () => {
    
    const taskOptionsContext = useContext(TaskOptionsContext) as ITaskOptions;
    const permissions = useTaskPermissions() as ITaskPermissions;    

    //Get the list of statuses that are allowed to be displayed
    const getOptionsForStatus = (statusId: number, mandatoryTask: boolean, allowComplete?: boolean): IOption[] =>
    { 
        if (!taskOptionsContext) {
            return [];
        }
    
        const currentStatus = taskOptionsContext.status.find(status => status.id === statusId);
        if (!currentStatus) {
            return [];
        }

        const canReopen = permissions?.canReopenCompleted ?? false;
        const canChange = permissions?.canChangeStatus ?? false;
        const canCancelMandatoryTasks = permissions?.canCancelMandatoryTasks ?? false;
    
        let statusOptions: IOption[] = [];
        if (!canChange || (!canReopen && currentStatus.id === TaskStatus.Completed)) {
            statusOptions.push({
                id: currentStatus.id,
                name: currentStatus.name,
                value: currentStatus.value
            });
            return statusOptions;
        }
    
        statusOptions = currentStatus.options.map(option => ({
            id: option.id,
            name: option.name,
            value: option.value
        }))// Filter out the status with ID 7 if mandatoryTask is true 
        .filter(option => canCancelMandatoryTasks || !(mandatoryTask && option.id === TaskStatus.Cancelled));

        // Append current status to the options
        statusOptions.push({
            id: currentStatus.id,
            name: currentStatus.name,
            value: currentStatus.value
        });

        const showComplete = allowComplete ?? true;
        if (!showComplete && currentStatus.id !== TaskStatus.Completed) {
            const index = statusOptions.findIndex(s => s.id === TaskStatus.Completed);
            if (index !== -1) {
                statusOptions.splice(index, 1);
            }
        }
    
        // Sort the options alphabetically by name
        return statusOptions.sort((a, b) => a.name.localeCompare(b.name));
    };

    //Say whether moving to complete is allowed for the given status
    const canCompleteFromStatus = (statusId: number) => {
        if (!permissions || !permissions.canChangeStatus) {
            return false;
        }
         if (!taskOptionsContext) {
            return false;
        }
    
        const statusValues = Object.values(taskOptionsContext.status);
        const currentStatus = statusValues.find(status => status.id === statusId);
    
        if (!currentStatus) {
            return false;
        }

        const completeOption = currentStatus.options.find(o => o.id === TaskStatus.Completed);

        if(completeOption)
            return true;

        return false;
    };
   
    return { getOptionsForStatus, canCompleteFromStatus }
};

export default useTaskStatusOptions;